// Normalising the media-chunk payloads.
//
// The chunks endpoint's exact envelope and field names aren't guaranteed, and chunk text is
// arbitrary document text — so it must never be interpolated into markup raw.

const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);

const firstNonEmpty = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");

/**
 * Pull the chunk list out of whatever the endpoint returned and give every entry stable
 * `id` / `text` / `sourceId` fields.
 *
 * Accepts `{chunks}`, `{items}`, `{data}`, `{data_items}`, `{chunks: {data: []}}` or a bare
 * array, and tolerates differing field names on each chunk.
 *
 * @returns {{id: string, text: string, sourceId: string|null, raw: object}[]}
 */
export const normalizeChunks = (results) => {
  const raw = Array.isArray(results)
    ? results
    : results?.chunks ?? results?.items ?? results?.data_items ?? results?.data ?? [];

  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.items)
        ? raw.items
        : [];

  return list.map((chunk, index) => ({
    id: String(
      firstNonEmpty(chunk?.public_id, chunk?.id, chunk?.chunk_id) ?? `chunk-${index}`
    ),
    text: String(
      firstNonEmpty(
        chunk?.text,
        chunk?.content,
        chunk?.chunk_text,
        chunk?.page_content,
        chunk?.document,
        chunk?.body
      ) ?? ""
    ),
    sourceId: firstNonEmpty(
      chunk?.source_media_chunk_id,
      chunk?.source_chunk_id,
      chunk?.source_id,
      chunk?.sourceId
    ) ?? null,
    raw: chunk,
  }));
};

/** One-line summary of why a chunk list is empty, for the dialog's empty state. */
export const describeChunkResponse = (results) => {
  if (!results) return "No response from the server.";
  if (results.success === false) return results.message || "The server reported a failure.";
  return "The server returned no chunks for this item.";
};
